import { Component } from '@theme/component';

const DAY = 86400;

/**
 * Top bar for landing pages with an optional countdown.
 *
 * Weekly mode rolls to the next chosen weekday/time in the visitor's local time (like an evergreen
 * "ends Sunday" promo). Fixed mode counts down to an absolute timestamp and can hide itself once over.
 * Also publishes `--landing-bar-height` so anchor targets can clear the sticky bar.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} [timer] - The timer pill.
 * @property {HTMLElement[]} [value] - The three number slots.
 * @property {HTMLElement[]} [unit] - The three unit labels.
 *
 * @extends Component<Refs>
 */
class LandingCountdownBar extends Component {
  /** @type {number | undefined} */
  #timeout;

  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry || !this.hasAttribute('data-sticky')) return;
    document.documentElement.style.setProperty('--landing-bar-height', `${entry.borderBoxSize[0]?.blockSize ?? 0}px`);
  });

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);
    if (this.refs.timer) this.#tick();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    clearTimeout(this.#timeout);
    document.documentElement.style.removeProperty('--landing-bar-height');
  }

  /** @returns {Date | null} */
  get #target() {
    const { mode, end, weekday, time } = this.dataset;

    if (mode === 'fixed') {
      const seconds = Number(end);
      return seconds > 0 ? new Date(seconds * 1000) : null;
    }

    const now = new Date();
    const [, hours = '23', minutes = '59'] = /^([01]?\d|2[0-3]):([0-5]\d)$/.exec(time?.trim() ?? '') ?? [];
    const daysToAdd = (Number(weekday) - now.getDay() + 7) % 7;
    const target = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate() + daysToAdd,
      Number(hours),
      Number(minutes),
      59
    );

    if (target <= now) target.setDate(target.getDate() + 7);
    return target;
  }

  #tick = () => {
    const target = this.#target;
    if (!target) return this.#end();

    // Weekly targets never end: once this one passes, the next tick rolls over to the following week.
    const left = Math.max(0, Math.floor((target.getTime() - Date.now()) / 1000));
    if (left === 0 && this.dataset.mode === 'fixed') return this.#end();

    const { daysLabel, dayLabel, hoursLabel, minutesLabel, secondsLabel } = this.dataset;
    const pad = (/** @type {number} */ n) => String(n).padStart(2, '0');

    /** @type {[string, string | undefined][]} */
    const parts =
      left >= DAY
        ? [
            [String(Math.floor(left / DAY)), Math.floor(left / DAY) === 1 ? dayLabel : daysLabel],
            [pad(Math.floor((left % DAY) / 3600)), hoursLabel],
            [pad(Math.floor((left % 3600) / 60)), minutesLabel],
          ]
        : [
            [pad(Math.floor(left / 3600)), hoursLabel],
            [pad(Math.floor((left % 3600) / 60)), minutesLabel],
            [pad(left % 60), secondsLabel],
          ];

    parts.forEach(([value, unit], index) => {
      const valueElement = this.refs.value?.[index];
      const unitElement = this.refs.unit?.[index];
      if (valueElement) valueElement.textContent = value;
      if (unitElement) unitElement.textContent = unit ?? '';
    });

    this.refs.timer?.removeAttribute('hidden');
    this.#timeout = setTimeout(this.#tick, 1000 - (Date.now() % 1000));
  };

  #end() {
    if (window.Shopify?.designMode) return;

    if (this.dataset.onEnd === 'hide-bar') {
      this.closest('.shopify-section')?.setAttribute('hidden', '');
      document.documentElement.style.removeProperty('--landing-bar-height');
    } else {
      this.refs.timer?.setAttribute('hidden', '');
    }
  }
}

if (!customElements.get('landing-countdown-bar')) {
  customElements.define('landing-countdown-bar', LandingCountdownBar);
}

/**
 * Sticky bottom call-to-action bar for landing pages.
 *
 * Slides in once the visitor has scrolled past `data-threshold` percent of the page, optionally
 * stays out of the way while the target offer section is on screen, and can be dismissed.
 *
 * @typedef {object} StickyRefs
 * @property {HTMLElement} bar - The sliding bar.
 *
 * @extends Component<StickyRefs>
 */
class LandingStickyCta extends Component {
  requiredRefs = ['bar'];

  #pastThreshold = false;
  #targetVisible = false;
  #dismissed = false;
  #editorSelected = false;
  #frame = 0;

  /** @type {IntersectionObserver | undefined} */
  #targetObserver;

  // Reports 0 while the bar isn't rendered (e.g. the mobile-only bar on desktop).
  #resizeObserver = new ResizeObserver(([entry]) => {
    this.#reserveSpace(entry?.borderBoxSize[0]?.blockSize ?? 0);
  });

  connectedCallback() {
    super.connectedCallback();

    this.#resizeObserver.observe(this.refs.bar);
    window.addEventListener('scroll', this.#onScroll, { passive: true });
    window.addEventListener('resize', this.#onScroll, { passive: true });
    this.#onScroll();

    const target = this.dataset.hideTarget ? document.getElementById(this.dataset.hideTarget) : null;
    if (target) {
      this.#targetObserver = new IntersectionObserver(([entry]) => {
        this.#targetVisible = Boolean(entry?.isIntersecting);
        this.#render();
      });
      this.#targetObserver.observe(target);
    }

    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:section:select', this.#onEditorSelect);
      document.addEventListener('shopify:section:deselect', this.#onEditorSelect);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('scroll', this.#onScroll);
    window.removeEventListener('resize', this.#onScroll);
    this.#resizeObserver.disconnect();
    document.documentElement.style.removeProperty('--landing-sticky-cta-height');
    this.#targetObserver?.disconnect();
    cancelAnimationFrame(this.#frame);
    document.removeEventListener('shopify:section:select', this.#onEditorSelect);
    document.removeEventListener('shopify:section:deselect', this.#onEditorSelect);
  }

  dismiss() {
    this.#dismissed = true;
    this.#reserveSpace(0);
    this.#render();
  }

  /**
   * Keeps room below the page so the bar never covers its end. Held while the bar is merely hidden
   * (so the page height doesn't jump as it slides in and out) and released once it's dismissed.
   * @param {number} height
   */
  #reserveSpace(height) {
    const value = this.#dismissed ? 0 : height;
    document.documentElement.style.setProperty('--landing-sticky-cta-height', `${value}px`);
  }

  #onScroll = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => {
      const scrollable = document.documentElement.scrollHeight - window.innerHeight;
      const progress = scrollable > 0 ? (window.scrollY / scrollable) * 100 : 100;
      this.#pastThreshold = progress >= Number(this.dataset.threshold ?? 0);
      this.#render();
    });
  };

  #onEditorSelect = (/** @type {Event} */ event) => {
    if (!(event.target instanceof Element) || !event.target.contains(this)) return;
    this.#editorSelected = event.type === 'shopify:section:select';
    this.#render();
  };

  #render() {
    const visible =
      this.#editorSelected || (this.#pastThreshold && !this.#targetVisible && !this.#dismissed);

    this.toggleAttribute('data-visible', visible);
    this.refs.bar.inert = !visible;
  }
}

if (!customElements.get('landing-sticky-cta')) {
  customElements.define('landing-sticky-cta', LandingStickyCta);
}

/**
 * GIF-style muted loop video that only downloads and plays while near the viewport, so a long
 * listicle with many clips doesn't fetch them all up front.
 *
 * @typedef {object} VideoRefs
 * @property {HTMLVideoElement} video - The video element.
 * @property {HTMLSourceElement[]} sources - Sources with a `data-src` to attach lazily.
 *
 * @extends Component<VideoRefs>
 */
class LandingVideo extends Component {
  requiredRefs = ['video', 'sources'];

  #observer = new IntersectionObserver(
    ([entry]) => {
      const { video } = this.refs;

      if (!entry?.isIntersecting) return video.pause();

      if (!video.currentSrc) this.#attachSources();

      video.play().catch(() => {});
    },
    { rootMargin: '200px 0px' }
  );

  connectedCallback() {
    super.connectedCallback();
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.#attachSources();
      this.refs.video.controls = true;
      return;
    }
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer.disconnect();
  }

  /** Browsers don't rescan `<source>` children when their `src` changes, so reload after attaching. */
  #attachSources() {
    for (const source of this.refs.sources) source.src = source.dataset.src ?? '';
    this.refs.video.load();
  }
}

if (!customElements.get('landing-video')) {
  customElements.define('landing-video', LandingVideo);
}
